// Tool sessions: per-end-user MCP URLs with an optional narrowing scope.
// The bearer token is shown exactly once (on creation) and never stored by the app.
import { useId, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useToolCategories } from '../api/catalog'
import { errorMessage } from '../api/errors'
import { qk } from '../api/queries'
import type { ToolSession, ToolSessionCreate, ToolSessionCreated, ToolSessionsResponse, Workspace } from '../api/types'
import { useSession } from '../context/SessionContext'
import { useFeature } from '../entitlements'
import { PlanNote } from '../entitlements/PlanNote'
import { CopyButton } from '../components/CopyButton'
import { Icon } from '../components/Icon'
import { Modal } from '../components/Modal'
import { formatDateTime } from '../components/format'
import { Badge, EmptyState, PageHeader, QueryState } from '../components/ui'

const END_USER_RE = /^[A-Za-z0-9._:@+-]{1,128}$/
const TTL_OPTIONS: [number, string][] = [
  [900, '15 minutes'],
  [3600, '1 hour'],
  [14400, '4 hours'],
  [86400, '24 hours'],
]

function splitList(v: string): string[] {
  return [...new Set(v.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean))]
}

function CreateSessionModal({ onClose, onCreated }: { onClose: () => void; onCreated: (s: ToolSessionCreated) => void }) {
  const id = useId()
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const { categories: vocab } = useToolCategories()
  const ws = useQuery({
    queryKey: qk.workspace(workspaceId),
    queryFn: () => apiFetch<Workspace>(`/workspaces/${workspaceId}`),
    enabled: !!workspaceId,
    retry: false,
  })
  // A session's categories must be a subset of the workspace's enabled set (when it has one).
  const enabled = ws.data?.enabled_categories ?? null
  const offered = enabled ? vocab.filter((c) => enabled.includes(c.slug)) : vocab

  const [endUser, setEndUser] = useState('')
  const [toolkits, setToolkits] = useState('')
  const [cats, setCats] = useState<string[]>([])
  const [ttl, setTtl] = useState(3600)
  const [touched, setTouched] = useState(false)

  const create = useMutation({
    mutationFn: (body: ToolSessionCreate) =>
      apiFetch<ToolSessionCreated>('/tool-sessions', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (s) => {
      void qc.invalidateQueries({ queryKey: qk.toolSessions(workspaceId) })
      onCreated(s)
    },
  })

  const endUserError = !endUser.trim()
    ? 'Required'
    : !END_USER_RE.test(endUser.trim())
      ? 'Letters, digits and . _ : @ + - only (max 128)'
      : null

  const submit = () => {
    setTouched(true)
    if (endUserError || create.isPending) return
    const body: ToolSessionCreate = { end_user: endUser.trim(), ttl_seconds: ttl }
    const tk = splitList(toolkits)
    if (tk.length) body.toolkits = tk
    if (cats.length) body.categories = cats
    create.mutate(body)
  }

  return (
    <Modal
      title="Create tool session"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" form={`${id}-form`} className="btn btn--primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create session'}
          </button>
        </>
      }
    >
      <form id={`${id}-form`} noValidate onSubmit={(e) => { e.preventDefault(); submit() }}>
        <div className="field">
          <label htmlFor={`${id}-eu`}>End user <span className="cell-sub">(required)</span></label>
          <input
            id={`${id}-eu`}
            value={endUser}
            placeholder="your-app-user-42"
            aria-invalid={touched && !!endUserError}
            aria-describedby={`${id}-eu-help`}
            onChange={(e) => setEndUser(e.target.value)}
          />
          <span id={`${id}-eu-help`} className="cell-sub">Your app’s id for this user (created if new).</span>
          {touched && endUserError && <span className="sf-error" role="alert">{endUserError}</span>}
        </div>
        <div className="field">
          <label htmlFor={`${id}-tk`}>Toolkits <span className="cell-sub">(optional)</span></label>
          <input id={`${id}-tk`} value={toolkits} placeholder="slack, google" onChange={(e) => setToolkits(e.target.value)} />
          <span className="cell-sub">Comma-separated toolkit names. Leave empty for no toolkit narrowing.</span>
        </div>
        <fieldset className="field" style={{ border: 0, padding: 0 }}>
          <legend className="sf-label">Categories <span className="cell-sub">(optional)</span></legend>
          <span className="cell-sub">
            The session only sees, and can only run, tools in these categories. Leave all unchecked for no category narrowing.
            {enabled && ' Limited to the categories enabled for this workspace.'}
          </span>
          <div className="cat-grid" role="group" aria-label="Session categories">
            {offered.map((c) => (
              <label key={c.slug} className="check">
                <input
                  type="checkbox"
                  checked={cats.includes(c.slug)}
                  onChange={(e) => setCats((p) => (e.target.checked ? [...p, c.slug] : p.filter((x) => x !== c.slug)))}
                />
                {c.display_name}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="field">
          <label htmlFor={`${id}-ttl`}>Expires after</label>
          <select id={`${id}-ttl`} value={ttl} onChange={(e) => setTtl(Number(e.target.value))}>
            {TTL_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {create.error ? <p className="form-error" role="alert">{errorMessage(create.error)}</p> : null}
      </form>
    </Modal>
  )
}

function CreatedModal({ session, onClose }: { session: ToolSessionCreated; onClose: () => void }) {
  const auth = session.mcp.headers.Authorization
  return (
    <Modal
      title="Tool session created"
      onClose={onClose}
      footer={<button type="button" className="btn btn--primary" onClick={onClose}>Done</button>}
    >
      <div className="secret-once">
        <p className="warn-note" role="note">Copy the token now. Symone stores only a hash and can’t show it again.</p>
        <span className="sf-label">MCP URL</span>
        <pre aria-label="Session MCP URL">{session.mcp.url}</pre>
        <CopyButton value={session.mcp.url} label="Copy URL" />
        <span className="sf-label">Authorization header</span>
        <pre aria-label="Session token">{auth}</pre>
        <CopyButton value={auth} label="Copy token" />
        <p className="cell-sub">Expires {formatDateTime(session.expires_at)}.</p>
      </div>
    </Modal>
  )
}

function scopeSummary(s: ToolSession): string {
  const parts: string[] = []
  if (s.scope.toolkits) parts.push(`toolkits: ${s.scope.toolkits.join(', ')}`)
  if (s.scope.tools) parts.push(`${s.scope.tools.length} tools`)
  if (s.scope.categories) parts.push(`categories: ${s.scope.categories.join(', ')}`)
  return parts.length ? parts.join(' · ') : 'Workspace policy only'
}

const STATUS_TONE = { active: 'success', revoked: 'neutral', expired: 'neutral' } as const

export function SessionsView() {
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<ToolSessionCreated | null>(null)
  // Tool sessions are part of `platform.end_users` (S4): create is disabled with the
  // server's remedy when the plan lacks it, or while the plan is unknown (fail closed).
  const platform = useFeature('platform.end_users')

  const list = useQuery({
    queryKey: qk.toolSessions(workspaceId),
    queryFn: () => apiFetch<ToolSessionsResponse>('/tool-sessions?limit=100'),
  })
  const revoke = useMutation({
    mutationFn: (id: string) => apiFetch(`/tool-sessions/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.toolSessions(workspaceId) }),
  })
  const sessions = list.data?.tool_sessions ?? []

  return (
    <div className="page">
      <PageHeader
        title="Sessions"
        count={list.data ? sessions.length : undefined}
        description="Tool sessions give one of your app’s users their own MCP URL, optionally narrowed to some toolkits or categories. Workspace policy always still applies."
        actions={
          !list.error && (
            <button
              type="button"
              className="btn btn--primary"
              disabled={!platform.allowed}
              aria-describedby={!platform.allowed && !platform.loading ? 'sessions-plan' : undefined}
              onClick={() => setCreating(true)}
            >
              <Icon name="plus" size={14} /> Create session
            </button>
          )
        }
      />
      <PlanNote gate={platform} id="sessions-plan">Tool sessions:</PlanNote>
      <QueryState isLoading={list.isLoading} error={list.error} what="tool sessions" onRetry={() => list.refetch()}>
        {revoke.error ? <p className="form-error" role="alert">{errorMessage(revoke.error)}</p> : null}
        {sessions.length === 0 ? (
          <EmptyState
            icon="clock"
            title="No tool sessions yet"
            body="Create a session to hand an end user a scoped MCP URL and token."
            sampleHeaders={['End user', 'Scope', 'Status', 'Expires']}
            sampleRows={[['customer-42', 'categories: crm', <Badge tone="success">active</Badge>, 'Nov 4, 2026']]}
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>End user</th>
                  <th>Scope</th>
                  <th>Status</th>
                  <th>Token</th>
                  <th>Expires</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id}>
                    <td className="cell-strong">{s.end_user ?? s.end_user_id}</td>
                    <td>{scopeSummary(s)}</td>
                    <td><Badge tone={STATUS_TONE[s.status]}>{s.status}</Badge></td>
                    <td><code>{s.token_prefix}…</code></td>
                    <td>{formatDateTime(s.expires_at)}</td>
                    <td className="cell-actions">
                      {s.status === 'active' && (
                        <button
                          type="button"
                          className="btn btn--sm btn--ghost-danger"
                          aria-label={`Revoke session for ${s.end_user ?? s.end_user_id}`}
                          disabled={revoke.isPending}
                          onClick={() => revoke.mutate(s.id)}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
      {creating && (
        <CreateSessionModal
          onClose={() => setCreating(false)}
          onCreated={(s) => {
            setCreating(false)
            setCreated(s)
          }}
        />
      )}
      {created && <CreatedModal session={created} onClose={() => setCreated(null)} />}
    </div>
  )
}
